import { useCallback } from 'react'
import { useBuilder } from './useBuilder'
import { notify } from '../../../notices'
import { normalizePage } from '../normalizePage'
import { saveDashboard } from '../hostAdapters'
import { getPageStateUnitData, getSharedFilterLock } from '../editable/unitRegistry'

/**
 * Récolte le contenu VIVANT des blocs que le canevas persiste lui-même.
 *
 * Un bloc sans `submission_url` (tous ceux d'un tableau de bord, un bloc de page
 * pas encore créé) n'a pas de DataSync active : il s'enregistre depuis
 * `bloc.data`. Mais un champ de formulaire du bloc — les filtres d'un widget
 * (filiale, pipeline, statut…) — écrit dans le STORE de l'unité, jamais dans
 * `bloc.data` : sans cette récolte, le save réécrit l'ancienne sélection.
 * Les blocs auto-persistés (DataSync) ne sont pas concernés : le registre ne
 * répond que pour une unité sans `submission_url`.
 */
const harvestData = (id, data) => {
  const live = getPageStateUnitData(id)
  let out = live ? { ...data, ...live } : data
  // …SAUF les filtres pilotés par la barre de filtres partagés du tableau de
  // bord : leur valeur est dans le store parce que l'URL l'y a injectée, pas
  // parce que quelqu'un a réglé ce widget. La récolter en ferait le réglage
  // PROPRE du widget — le filtre partagé deviendrait son défaut et le vider ne
  // rendrait plus le périmètre complet.
  const locked = getSharedFilterLock(id)
  if (out !== data && locked.length) {
    const kept = { ...out }
    for (const key of locked) {
      if (Object.prototype.hasOwnProperty.call(data, key)) kept[key] = data[key]
      else delete kept[key]
    }
    out = kept
  }
  // Un bloc de grille (`GridLayout` / `DashboardGridLayout`) porte ses widgets
  // dans `cells` : chaque cellule est montée comme une unité à part entière,
  // avec son propre store — c'est LÀ que vivent les filtres d'un tableau de
  // bord, la grille n'étant qu'un conteneur.
  if (Array.isArray(out?.cells)) {
    let changed = false
    const cells = out.cells.map((c) => {
      if (!c?.id) return c
      const base = c.data ?? {}
      const next = harvestData(c.id, base)
      if (next === base) return c
      changed = true
      return { ...c, data: next }
    })
    if (changed) out = { ...out, cells }
  }
  return out
}

export const harvestBlocs = (blocs) => (blocs ?? []).map((b) => {
  const base = b.data ?? {}
  const data = harvestData(b.id, base)
  return data === base ? b : { ...b, data }
})

export const useBuilderSave = () => {
  const { page, setPage, setIsSaving, setIsDirty, host } = useBuilder()

  const savePage = useCallback(async () => {
    setIsSaving(true)
    try {
      // A dashboard host persists all its blocs (content + layout) as one
      // `field_layout` JSON blob via the decoupled API — no per-child node, no
      // orchestrated proxy : un seul POST suffit. Le contenu vient de
      // `page.blocs` (chemin état-de-page) ET du store des unités montées, que
      // `harvestBlocs` réunit — cf. sa doc.
      if (host) {
        const blocs = harvestBlocs(page.blocs)
        try {
          await saveDashboard(page.hostMeta, blocs)
        } catch (err) {
          notify({ type: 'error', message: err?.message || 'Échec de la sauvegarde du tableau de bord.' })
          throw err
        }
        // L'état du canevas doit refléter ce qui vient d'être écrit : sinon un
        // re-montage du bloc (sortie du mode édition, changement de disposition)
        // le ré-amorcerait avec l'ancienne sélection de filtres. Seuls les blocs
        // réellement récoltés changent d'identité — les autres gardent la leur,
        // donc aucun re-seed inutile.
        const harvested = new Map(blocs.map((b) => [b.id, b.data]))
        setPage(prev => ({
          ...prev,
          blocs: prev.blocs.map((b) => {
            const data = harvested.get(b.id)
            return data && data !== b.data ? { ...b, data } : b
          }),
        }))
        setIsDirty(false)
        return true
      }
      // Send each bloc with its `id` (stable builderId) and optional `nid`
      // (Drupal node id). The backend uses `nid` to update existing children
      // vs create new ones; absent `nid` triggers a fresh node creation.
      //
      // `modelFor` n'est PAS envoyé : clé absente ⇒ le back laisse
      // `field_model_for` intact. Il s'écrit depuis l'onglet « Modèle » du type
      // de contenu (/api/builder/object-def/model), seul endroit qui tient la
      // règle 1 type = 1 modèle — une page ouverte dans un autre onglet
      // l'écraserait sinon avec la valeur qu'elle avait au chargement.
      // `published` : statut de publication (case à cocher du header). Toujours
      // envoyé sur ce chemin — le backend n'écrit le statut que si la clé est
      // là, ce qui laisse les autres appelants (agent, scripts) sans effet.
      const payload = {
        // `nid` identifie la page — le slug, lui, est une DONNÉE modifiable
        // (panneau « Adresse »). Sans nid, le backend retrouve la page par son
        // slug et un renommage créerait une seconde page au lieu de renommer.
        nid: page.pageNid ?? null,
        slug: page.slug,
        title: page.title,
        published: page.published !== false,
        // Drapeau « modèle ». Toujours envoyé sur ce chemin — le back n'écrit
        // le champ que si la clé est là, ce qui laisse les autres appelants
        // (assistant IA, scripts) sans effet sur lui.
        isTemplate: page.isTemplate === true,
        // Rattachement au site : le backend écrit la relation de groupe. Envoyé
        // seulement quand la page en porte un (ou l'a explicitement détaché),
        // le backend laissant le rattachement intact si la clé est absente.
        ...(page.siteId !== undefined ? { siteId: page.siteId ?? null } : {}),
        meta: page.meta && typeof page.meta === 'object' ? page.meta : {},
        // Catégories du site saisies dans le bandeau. Toujours envoyées sur ce
        // chemin — le back n'écrit le champ que si la clé est là, ce qui laisse
        // sans effet les autres appelants (assistant IA, scripts) et évite
        // qu'un save de blocs balaie les catégories à leur insu.
        tags: Array.isArray(page.tags) ? page.tags : [],
        // Anciennes adresses menant à cette page. Envoyé seulement quand
        // l'état en porte une liste — le back laisse le champ intact si la clé
        // est absente, ce qui évite qu'un appelant qui les ignore (assistant IA,
        // script, hôte tableau de bord) les balaie à son insu. Le RENOMMAGE, lui,
        // ajoute la sienne côté serveur : le front n'a rien à deviner.
        ...(Array.isArray(page.redirects) ? { redirects: page.redirects } : {}),
        // Le renommage pose SA redirection côté serveur ; le panneau « Adresse »
        // permet de la refuser. Seul le REFUS voyage : clé absente ⇒ le back
        // garde son comportement par défaut, donc les autres appelants
        // (assistant IA, scripts) ne perdent pas la reprise automatique.
        ...(page.redirectOnRename === false ? { redirectOnRename: false } : {}),
        // Idem hôte page : un bloc pas encore créé (donc sans DataSync) porte
        // ses éventuelles écritures de store dans le store de l'unité.
        blocs: harvestBlocs(page.blocs).map(b => ({
          id: b.id,
          nid: b.nid ?? null,
          type: b.type,
          data: b.data ?? {},
          hiddenOn: b.hiddenOn ?? [],
        })),
      }

      const res = await fetch('/api/builder/save', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      })

      if (res.status === 422) {
        // Entity validation failed server-side. The backend rolled back the
        // transaction, so nothing was persisted. Surface each violation as
        // an error toast and bail out without flipping isDirty.
        const body = await res.json().catch(() => null)
        const violations = Array.isArray(body?.violations) ? body.violations : []
        if (violations.length === 0) {
          notify({ type: 'error', message: 'Échec de la validation côté serveur.' })
        }
        else {
          for (const v of violations) {
            const where = v.context?.type
              ? `[${v.context.type}] `
              : v.context?.page
                ? `[page ${v.context.page}] `
                : ''
            const path = v.path ? `${v.path}: ` : ''
            notify({
              type: 'error',
              message: `${where}${path}${v.message}`,
            })
          }
        }
        return false
      }

      if (!res.ok) {
        const body = await res.json().catch(() => null)
        const msg = body?.message || `Échec de la sauvegarde (HTTP ${res.status})`
        notify({ type: 'error', message: msg })
        throw new Error(msg)
      }

      const result = await res.json()

      // Un bloc retiré de cette page mais encore utilisé ailleurs n'est PAS
      // supprimé (cf. countInboundRefs côté serveur) : le dire, sinon l'éditeur
      // croit avoir détruit un contenu qui vit toujours sur d'autres pages.
      const detached = Array.isArray(result?.detachedUnits) ? result.detachedUnits.length : 0
      if (detached > 0) {
        notify({
          type: 'info',
          message: detached > 1
            ? `${detached} blocs retirés de cette page sont conservés : ils sont utilisés ailleurs.`
            : 'Le bloc retiré de cette page est conservé : il est utilisé ailleurs.',
        })
      }

      // The save returns the SAME decoupled shape as the page load, so we run
      // the SAME normalizer. This pulls in not just the freshly-assigned `nid`s
      // but each bloc's `syncUrls` (`submission_url` …) — so a just-created
      // block becomes a per-unit DataSync unit immediately, with no reload.
      // Reconciled by stable builderId to preserve any local-only fields.
      const normalized = normalizePage(result)
      setPage(prev => {
        const prevById = Object.fromEntries((prev.blocs ?? []).map(b => [b.id, b]))
        return {
          ...prev,
          pageNid: normalized.pageNid ?? prev.pageNid,
          title: normalized.title || prev.title,
          slug: normalized.slug || prev.slug,
          published: normalized.published,
          isTemplate: normalized.isTemplate,
          siteId: normalized.siteId ?? null,
          siteName: normalized.siteName ?? '',
          // Le refus de redirection valait pour CE save : le renommage suivant
          // repart sur la reprise automatique, comme partout ailleurs.
          redirectOnRename: true,
          meta: normalized.meta ?? prev.meta,
          // Les catégories reviennent du back FILTRÉES : orbit_site n'a gardé
          // que celles du site de la page. On reprend donc sa version, pas la
          // locale — sinon le bandeau continuerait d'afficher une catégorie
          // qui n'a pas été enregistrée.
          tags: normalized.tags ?? prev.tags,
          modelFor: normalized.modelFor ?? prev.modelFor,
          blocs: normalized.blocs.map(b => {
            const prevBloc = prevById[b.id]
            // An EXISTING unit's content (`data`/`hiddenOn`) is owned by its
            // per-unit DataSync — the local value is authoritative (just edited
            // + flushed), while the proxy's render of it may be concurrent with
            // that POST and thus stale. So for an existing bloc keep the local
            // content and take only the structural fields (nid, syncUrls, type)
            // from the response. A brand-new bloc (no prior nid) has no DataSync
            // yet → the proxy response IS authoritative for it.
            if (prevBloc && prevBloc.nid != null) {
              return {
                ...prevBloc,
                nid: b.nid ?? prevBloc.nid,
                type: b.type ?? prevBloc.type,
                syncUrls: b.syncUrls ?? prevBloc.syncUrls,
              }
            }
            return { ...prevBloc, ...b }
          }),
        }
      })

      setIsDirty(false)
      return true
    } catch (err) {
      console.error('Builder save error:', err)
      throw err
    } finally {
      setIsSaving(false)
    }
  }, [page, host, setPage, setIsSaving, setIsDirty])

  return { savePage }
}
